import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  Tabs,
  Tab
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  Gavel as GavelIcon,
  VerifiedUser as VerifiedUserIcon,
  BarChart as BarChartIcon
} from '@mui/icons-material';

/**
 * @component ComplianceFeatures
 * @description Compliance and regulatory features component for ethical mineral trading
 *
 * SEO Considerations:
 * - Highlights regulatory compliance for ethical mineral sourcing
 * - Includes detailed information about EU Conflict Minerals Regulation, OECD Due Diligence, and US Dodd-Frank Act
 * - Demonstrates ESG (Environmental, Social, Governance) reporting capabilities
 * - Uses semantic HTML structure with proper heading hierarchy
 * - Includes keyword-rich content about mineral compliance and ethical sourcing
 * - Provides comprehensive information about regulatory frameworks relevant to mineral trading
 */
const ComplianceFeatures = () => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Compliance frameworks
  const complianceFrameworks = [
    {
      name: 'EU Conflict Minerals Regulation',
      description: 'Ensures EU importers of 3TG minerals (tin, tungsten, tantalum, and gold) meet international responsible sourcing standards.',
      requirements: [
        'Supply chain due diligence',
        'Risk management',
        'Third-party audits',
        'Annual reporting'
      ],
      icon: <GavelIcon />
    },
    {
      name: 'OECD Due Diligence Guidance',
      description: 'Provides detailed recommendations to help companies respect human rights and avoid contributing to conflict through their mineral purchasing decisions.',
      requirements: [
        'Establish strong company management systems',
        'Identify and assess risks in the supply chain',
        'Design and implement a strategy to respond to identified risks',
        'Carry out independent third-party audit',
        'Report annually on supply chain due diligence'
      ],
      icon: <AssignmentIcon />
    },
    {
      name: 'US Dodd-Frank Act (Section 1502)',
      description: 'Requires companies to disclose their use of conflict minerals originating from the DRC or adjoining countries.',
      requirements: [
        'Determine if conflict minerals are necessary to functionality',
        'Conduct country of origin inquiry',
        'Exercise due diligence on source and chain of custody',
        'File conflict minerals report with SEC'
      ],
      icon: <GavelIcon />
    },
    {
      name: 'Responsible Minerals Assurance Process (RMAP)',
      description: 'Helps companies make informed choices about conflict-free minerals in their supply chains.',
      requirements: [
        'Independent third-party assessment of smelter/refiner management systems',
        'Material control mechanisms',
        'Documentation and traceability',
        'Grievance mechanism'
      ],
      icon: <VerifiedUserIcon />
    }
  ];

  // ESG reporting features
  const esgFeatures = [
    {
      category: 'Environmental',
      features: [
        'Carbon footprint tracking across the supply chain',
        'Water usage and pollution monitoring',
        'Land use and biodiversity impact assessment',
        'Energy consumption and renewable energy adoption metrics',
        'Waste management and recycling rate tracking'
      ]
    },
    {
      category: 'Social',
      features: [
        'Labor practices and human rights compliance',
        'Community impact and development initiatives',
        'Worker health and safety metrics',
        'Gender equality and diversity tracking',
        'Fair wage verification and monitoring'
      ]
    },
    {
      category: 'Governance',
      features: [
        'Anti-corruption and anti-bribery compliance',
        'Transparent ownership and control structures',
        'Ethical business conduct verification',
        'Board diversity and independence metrics',
        'Stakeholder engagement and grievance mechanisms'
      ]
    }
  ];

  // Reporting templates
  const reportingTemplates = [
    {
      name: 'EU Conflict Minerals Report',
      description: 'Comprehensive reporting template aligned with EU regulations',
      format: 'Excel, PDF',
      sections: ['Company Information', 'Supply Chain Mapping', 'Risk Assessment', 'Due Diligence Measures', 'Independent Audit Results']
    },
    {
      name: 'OECD 5-Step Framework Report',
      description: 'Structured reporting based on the OECD 5-step framework',
      format: 'Excel, PDF, Web Form',
      sections: ['Management Systems', 'Risk Identification', 'Risk Mitigation', 'Audit Results', 'Public Reporting']
    },
    {
      name: 'ESG Performance Report',
      description: 'Comprehensive ESG metrics and performance indicators',
      format: 'Excel, PDF, Interactive Dashboard',
      sections: ['Environmental Metrics', 'Social Impact', 'Governance Structure', 'Sustainability Goals', 'Improvement Initiatives']
    },
    {
      name: 'Supplier Due Diligence Report',
      description: 'Detailed assessment of supplier compliance and performance',
      format: 'Excel, PDF',
      sections: ['Supplier Information', 'Compliance Status', 'Risk Assessment', 'Corrective Actions', 'Verification Results']
    }
  ];

  return (
    <Paper
      aria-label="Compliance and regulatory features for ethical mineral trading"
      sx={{
        position: 'relative',
        width: '100%',
        borderRadius: 3,
        border: '2px solid #f5e5a5',
        borderLeft: '6px solid #eab308',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        background: 'linear-gradient(to right, #fffef0, #fffde7)',
        p: 3,
        mt: 0,
        mb: 0,
        overflow: 'hidden'
      }}
    >
      <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
        Ethical Mineral Compliance & Regulatory Features
      </Typography>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{
          mb: 4,
          '& .MuiTab-root': {
            fontWeight: 'bold',
            borderRadius: '8px 8px 0 0',
          },
          '& .Mui-selected': {
            bgcolor: 'rgba(234, 179, 8, 0.1)',
          }
        }}
      >
        <Tab label="Regulatory Compliance" icon={<GavelIcon />} iconPosition="start" />
        <Tab label="ESG Reporting" icon={<BarChartIcon />} iconPosition="start" />
        <Tab label="Report Templates" icon={<AssignmentIcon />} iconPosition="start" />
      </Tabs>

      {/* Regulatory Compliance Tab */}
      {activeTab === 0 && (
        <Box>
          <Typography variant="subtitle1" paragraph>
            Our platform ensures compliance with major international regulations for ethical mineral sourcing and trading from Africa, including conflict minerals regulations and due diligence requirements:
          </Typography>

          {complianceFrameworks.map((framework, index) => (
            <Accordion
              key={framework.name}
              defaultExpanded={index === 0}
              sx={{
                mb: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                '&:before': { display: 'none' },
                borderRadius: '8px !important',
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  bgcolor: 'rgba(234, 179, 8, 0.1)',
                  '&.Mui-expanded': {
                    borderBottom: '1px solid rgba(0,0,0,0.1)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ mr: 2, color: '#eab308' }}>
                    {framework.icon}
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {framework.name}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" paragraph>
                  {framework.description}
                </Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
                  Key Requirements:
                </Typography>

                <List dense disablePadding>
                  {framework.requirements.map((req, idx) => (
                    <ListItem key={idx} disablePadding sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={req} />
                    </ListItem>
                  ))}
                </List>

                <Box sx={{ mt: 2, textAlign: 'right' }}>
                  <Chip
                    label="Automated Compliance"
                    color="success"
                    size="small"
                    icon={<CheckCircleIcon />}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}

          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              sx={{ borderRadius: 50, px: 4 }}
            >
              Compliance Assessment
            </Button>
          </Box>
        </Box>
      )}

      {/* ESG Reporting Tab */}
      {activeTab === 1 && (
        <Box>
          <Typography variant="subtitle1" paragraph>
            Our platform provides comprehensive ESG (Environmental, Social, Governance) reporting capabilities for ethical mineral supply chains, helping companies demonstrate their commitment to sustainable and responsible sourcing:
          </Typography>

          <Grid container spacing={3}>
            {esgFeatures.map((category) => (
              <Grid item xs={12} md={4} key={category.category}>
                <Paper
                  sx={{
                    p: 2,
                    height: '100%',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    borderRadius: 2,
                    bgcolor: 'rgba(255, 255, 255, 0.7)',
                    borderTop: '4px solid',
                    borderColor: category.category === 'Environmental'
                      ? '#4caf50'
                      : category.category === 'Social'
                        ? '#2196f3'
                        : '#9c27b0'
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      fontWeight: 'bold',
                      color: category.category === 'Environmental'
                        ? '#4caf50'
                        : category.category === 'Social'
                          ? '#2196f3'
                          : '#9c27b0'
                    }}
                  >
                    {category.category}
                  </Typography>

                  <List dense disablePadding>
                    {category.features.map((feature, idx) => (
                      <ListItem key={idx} disablePadding sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon
                            fontSize="small"
                            sx={{
                              color: category.category === 'Environmental'
                                ? '#4caf50'
                                : category.category === 'Social'
                                  ? '#2196f3'
                                  : '#9c27b0'
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              sx={{ borderRadius: 50, px: 4 }}
            >
              Generate ESG Report
            </Button>
          </Box>
        </Box>
      )}

      {/* Report Templates Tab */}
      {activeTab === 2 && (
        <Box>
          <Typography variant="subtitle1" paragraph>
            Our platform provides ready-to-use reporting templates for ethical mineral sourcing, regulatory compliance, and stakeholder communication, ensuring transparency throughout the supply chain from African mines to global markets:
          </Typography>

          <Grid container spacing={2}>
            {reportingTemplates.map((template) => (
              <Grid item xs={12} sm={6} key={template.name}>
                <Paper
                  sx={{
                    p: 2,
                    height: '100%',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    borderRadius: 2,
                    bgcolor: 'rgba(255, 255, 255, 0.7)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.12)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AssignmentIcon sx={{ mr: 1, color: '#eab308' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {template.name}
                    </Typography>
                  </Box>

                  <Typography variant="body2" paragraph>
                    {template.description}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', mr: 1 }}>
                      Format:
                    </Typography>
                    <Typography variant="caption">
                      {template.format}
                    </Typography>
                  </Box>

                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                    Sections:
                  </Typography>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    {template.sections.map((section) => (
                      <Chip
                        key={section}
                        label={section}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    ))}
                  </Box>

                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ borderRadius: 50 }}
                  >
                    Preview Template
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              sx={{ borderRadius: 50, px: 4 }}
            >
              Access All Templates
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default ComplianceFeatures;
